import React, { useCallback, useRef, useState } from 'react';
import './LightPull.css';

/* A beaded darkroom light pull. Grab it and drag, or tap it.
   Geometry is in SVG user units, and the SVG is 1:1 with CSS px. */
const REST_Y = 118;    // where the ring hangs when nobody is touching it
const MAX_PULL = 92;   // the furthest the chain will travel
const SOFT = 60;       // past this the chain starts to fight back
const TRIGGER = 38;    // pull at least this far to actually throw the switch
const TAP_SLOP = 6;    // movement under this counts as a tap, not a drag

// The chain runs well above the top of the frame so that pulling it down
// reveals more beads instead of tearing it off the ceiling.
const BEADS = [];
for (let y = REST_Y; y > -340; y -= 9) BEADS.push(y);

const resist = (d) => (d <= SOFT ? d : SOFT + (d - SOFT) * 0.34);

export default function LightPull({ on, onToggle, hint }) {
  const [pull, setPull] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [swaying, setSwaying] = useState(false);
  const [used, setUsed] = useState(false);
  const drag = useRef({ startY: 0, moved: 0 });
  const swayTimer = useRef(null);
  // A pointer gesture also produces a click; this stops it counting twice.
  const pointerHandled = useRef(false);
  const clearTimer = useRef(null);

  const settle = useCallback(() => {
    setPull(0);
    setSwaying(true);
    clearTimeout(swayTimer.current);
    swayTimer.current = setTimeout(() => setSwaying(false), 900);
  }, []);

  const down = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { startY: e.clientY, moved: 0 };
    pointerHandled.current = false;
    setUsed(true);
    setSwaying(false);
    setDragging(true);
  };

  const move = (e) => {
    if (!dragging) return;
    const dy = e.clientY - drag.current.startY;
    drag.current.moved = Math.max(drag.current.moved, Math.abs(dy));
    setPull(Math.max(0, Math.min(resist(Math.max(0, dy)), MAX_PULL)));
  };

  const up = () => {
    if (!dragging) return;
    setDragging(false);
    pointerHandled.current = true;
    clearTimeout(clearTimer.current);
    clearTimer.current = setTimeout(() => { pointerHandled.current = false; }, 400);
    if (drag.current.moved < TAP_SLOP) {
      // A tap: play the pull for them rather than doing nothing.
      setPull(TRIGGER + 16);
      onToggle();
      setTimeout(settle, 160);
      return;
    }
    if (pull >= TRIGGER) onToggle();
    settle();
  };

  // Keyboard (Enter/Space on the button) and assistive activation arrive as a
  // plain click, with no pointer gesture in front of them.
  const click = () => {
    if (pointerHandled.current) return;
    setUsed(true);
    setPull(TRIGGER + 16);
    onToggle();
    setTimeout(settle, 160);
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={hint}
      className={[
        'pull',
        dragging ? 'pull--dragging' : '',
        swaying ? 'pull--swaying' : '',
        on ? 'pull--on' : 'pull--off',
      ].join(' ')}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
      onClick={click}
    >
      <svg width="44" height="340" viewBox="0 0 44 340" aria-hidden="true">
        <g className="pull__sway">
          <g className="pull__chain" style={{ transform: `translateY(${pull}px)` }}>
            {/* the links behind the balls, so the chain reads as one piece */}
            <line x1="22" y1="-340" x2="22" y2={REST_Y + 10} className="pull__cord" />
            {BEADS.map((y) => (
              <circle key={y} cx="22" cy={y} r="3.4" className="pull__bead" />
            ))}
            <circle cx="22" cy={REST_Y + 20} r="7" className="pull__ring" />
          </g>
        </g>
      </svg>
      <span className={`pull__label ${used ? 'pull__label--gone' : ''}`}>Pull</span>
    </button>
  );
}
